import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { BehaviorSubject, merge } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { PostAutoCompleteRequestDto } from '@shared/dtos/post-auto-complete-request.dto';
import { ADDRESS_COMPLETION_OPTIONS } from '../../address-completion.token';
import { AddressCompletionOptions } from './../../interfaces/address-completion-options.interface';
import { AddressCompletionService } from '../../services/address-completion.service';
import { CantonDto } from '@shared/dtos/canton.dto';

@Component({
  selector: 'form-address-completion',
  templateUrl: './address-completion.component.html',
  styleUrls: ['./address-completion.component.scss'],
})
export class AddressCompletionComponent implements OnInit {
  readonly SWISS_COUNTRY_CODE = 'ch';
  readonly ADDRESS_CONTROL_NAMES = [
    'country',
    'street',
    'streetNumber',
    'postCode',
    'locality',
    'canton',
  ];
  @Input() formGroup: UntypedFormGroup;
  @Input() countries: any[] | null;
  @Input() isSwissOnly: boolean = false;
  @Input() isMandatory: boolean = true;
  @Input() isCleanable: boolean = false;
  @ViewChild(MatAutocompleteTrigger) trigger: MatAutocompleteTrigger;
  cantonsCaraList: CantonDto[];
  usePostApi: boolean = false;
  address: { [key: string]: BehaviorSubject<string[]> } = {
    streets: new BehaviorSubject<string[]>([]),
    streetNumbers: new BehaviorSubject<string[]>([]),
    postCodes: new BehaviorSubject<string[]>([]),
    localities: new BehaviorSubject<string[]>([]),
    canton: new BehaviorSubject<string[]>([]),
  };

  constructor(
    @Inject(ADDRESS_COMPLETION_OPTIONS)
    private readonly options: AddressCompletionOptions,
    private addressCompletionService: AddressCompletionService
  ) {}

  ngOnInit(): void {
    // Récupération de la liste des cantons acceptés
    this.addressCompletionService.getCantonsList().subscribe((res) => {
      this.cantonsCaraList = res;
    });

    // When the country changes, we reset the address
    if (this.isSwissOnly) {
      this.usePostApi = true;
    } else {
      this.setUsePostApi();

      this.formGroup.get('country')?.valueChanges.subscribe((value) => {
        
        if(!this.formGroup.pristine) {
          this.formGroup.get('postCode')?.setValue(null, { emitEvent: false });
          this.formGroup.get('locality')?.setValue(null, { emitEvent: false });
          this.formGroup.get('street')?.setValue(null, { emitEvent: false });
          this.formGroup.get('canton')?.setValue(null, { emitEvent: false });
          this.formGroup
            .get('streetNumber')
            ?.setValue(null, { emitEvent: false });
        }
        this.setUsePostApi();
      });
    }

    this.formGroup
      .get('postCode')
      ?.valueChanges.pipe(
        filter(() => {
          return this.usePostApi;
        }),
        debounceTime(200)
      )
      .subscribe(async () => {
        if(!this.formGroup.pristine) {
          // When the post code changes, we reset the locality, street and street number
          this.formGroup.get('locality')?.setValue(null, { emitEvent: false });
          this.formGroup.get('street')?.setValue(null, { emitEvent: false });
          this.formGroup.get('canton')?.setValue(null, { emitEvent: false });
          this.formGroup
            .get('streetNumber')
            ?.setValue(null, { emitEvent: false });
        }

        await this.onAddressChanges().then( ()=> {
         // If only one locality match the postCode, we select the locality
          this.address.localities.subscribe((localities) => {
            if (localities.length === 1) {
              this.formGroup
                .get('locality')
                ?.setValue(localities[0], { emitEvent: false });
            }
          });

          this.address.canton.subscribe((canton) => {
            if (canton.length === 1) {
              this.formGroup
                .get('canton')
                ?.setValue(canton[0], { emitEvent: false });
            }
          });
        });

      });

    merge(
      (this.formGroup.get('locality') as UntypedFormControl).valueChanges,
      (this.formGroup.get('street') as UntypedFormControl).valueChanges,
      (this.formGroup.get('streetNumber') as UntypedFormControl).valueChanges,
      (this.formGroup.get('canton') as UntypedFormControl).valueChanges,
    )
      .pipe(
        filter(() => {
          return this.usePostApi;
        }),

        debounceTime(200)
      )
      .subscribe(async () => {
        await this.onAddressChanges();
      });
  }

  reset() {
    this.formGroup.reset({},{ emitEvent: false });
  }

  /**
   * When the address changes, we autocomplete the address from the post API
   */
  private onAddressChanges(): Promise<void> {
    return new Promise((resolve, reject) => {

      if (this.isCleanable && this.isAllControlEmpty())
        return resolve()

      const request: PostAutoCompleteRequestDto = {
        request: {
          ONRP: 0,
          ZipCode: this.formGroup.get('postCode')?.value || '',
          Canton: this.formGroup.get('canton')?.value || '',
          ZipAddition: '',
          TownName: this.formGroup.get('locality')?.value || '',
          STRID: 0,
          StreetName: this.formGroup.get('street')?.value || '',
          HouseKey: 0,
          HouseNo: this.formGroup.get('streetNumber')?.value || '',
          HouseNoAddition: '',
        },
        zipOrderMode: 0,
      };

      this.addressCompletionService.autoComplete(request).subscribe((res) => {
        if (res.AutoCompleteResult && res.AutoCompleteResult.length > 0) {
          const streets = res.AutoCompleteResult.filter(
            (value) => !!value.StreetName
          )
            .map((res) => res.StreetName)
            .filter(this.onlyUnique);
          this.address.streets.next(streets);

          const streetNumbers = res.AutoCompleteResult.filter(
            (value) => !!value.HouseNo
          )
            .map((res) => res.HouseNo + res.HouseNoAddition)
            .filter(this.onlyUnique);
          this.address.streetNumbers.next(streetNumbers);

          const cantons = res.AutoCompleteResult.filter(
            (value) => !!value.Canton
          )
            .map((cantonDto) => {
              return cantonDto.Canton;
            })
            .filter(this.onlyUnique);

          if (this.cantonsCaraList && this.cantonsCaraList.length > 0) {
            let isCantonCara = this.cantonsCaraList.map((cantonDto) => cantonDto.Canton).includes(cantons[0]);
            cantons[0] = isCantonCara ? cantons[0] : 'Autre';
          }
          this.address.canton.next(cantons);

          const localities = res.AutoCompleteResult.filter((value) => {
            return (
              value &&
              (value.ZipAddition === '00' || !/\d/.test(value.TownName))
            );
          })
            .map((res) => res.TownName)
            .filter(this.onlyUnique);
          this.address.localities.next(localities as any);

          const postCodes = res.AutoCompleteResult.filter(
            (value) => value && value.ZipAddition === '00'
          )
            .map((res) => res.ZipCode)
            .filter(this.onlyUnique);
          this.address.postCodes.next(postCodes);
        }

        if (this.formGroup.valid) {
          this.addressCompletionService
            .validateAddress(this.formGroup.value)
            .subscribe((valid) => {
              if (!valid) {
                this.formGroup.setErrors({ addressInvalid: true });
              }
            });
        }

        resolve();
      });
    });
  }

  private isAllControlEmpty(): boolean {    
    let ctrls = this.formGroup;
    return (ctrls && (
      !(ctrls?.get('postCode')?.value) &&
      !(ctrls?.get('locality')?.value) &&
      !(ctrls?.get('street')?.value) &&
      !(ctrls?.get('streetNumber')?.value) &&
      !(ctrls?.get('canton')?.value) ));
  }

  private setUsePostApi(): void {
    const prevValue = this.usePostApi;
    this.usePostApi =
      this.formGroup.get('country')?.value === this.SWISS_COUNTRY_CODE;
    if (prevValue !== this.usePostApi) {
      this.address.streets.next([]);
      this.address.streetNumbers.next([]);
      this.address.postCodes.next([]);
      this.address.localities.next([]);
      this.address.canton.next([]);
    }
  }

  private onlyUnique<T>(value: T, index: number, self: T[]): boolean {
    return self.indexOf(value) === index;
  }
}
